import React, { Fragment, useState, useEffect } from 'react';
import { Container, TabContent, TabPane, Nav, NavItem, NavLink, Card, CardBody, Row, Col } from 'reactstrap';
import { Breadcrumbs } from '../../../../AbstractElements';
import classnames from 'classnames';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import { fetchFormData } from '../../../../Api/ApiMethods';
import AccidentFileInformations from './AccidentFileInformations';
import ArbitrationInformations from './ArbitrationInformations';
import InterlocutoryRulingInformations from './InterlocutoryRulingInformations';
import ExpertWitnessReportInformations from './ExpertWitnessReportInformations';
import RulingInformations from './RulingInformations';
import ReapplicationInformations from './ReapplicationInformations';
import EnforcementInformations from './EnforcementInformations';
import CompensationInformations from './CompensationInformations';

const Arbitration = () => {
    const { id } = useParams();
    const [activeTab, setActiveTab] = useState('1');
    const [arbitrationData, setArbitrationData] = useState({});
    const [loading, setLoading] = useState(true);

    // API endpoint
    const arbitrationApiUrl = `${process.env.REACT_APP_LACIVERT_API_ENDPOINT}/arbitration/${id}`;

    // Tab definitions
    const tabs = [
        { id: '1', title: 'Kaza Dosyaları', icon: 'fa fa-folder' },
        { id: '2', title: 'Tahkim Bilgileri', icon: 'fa fa-info-circle' },
        { id: '3', title: 'Ara Kararlar', icon: 'fa fa-gavel' },
        { id: '4', title: 'Bilirkişi Raporları', icon: 'fa fa-file-text' },
        { id: '5', title: 'Kararlar', icon: 'fa fa-check-circle' },
        { id: '6', title: 'Tekrar Başvuru Bilgileri', icon: 'fa fa-repeat' },
        { id: '7', title: 'İcra Bilgileri', icon: 'fa fa-balance-scale' },
        { id: '8', title: 'Kapaklar', icon: 'fa fa-folder-open' }
    ];

    // Function to determine which tabs should be visible based on arbitration state and existing data
    const getVisibleTabIds = () => {
        // İlk olarak isSentenced kontrolü yap
        const isSentenced = arbitrationData?.accidentFile?.isSentenced;
        
        // Eğer isSentenced false ise sadece Kaza Dosyaları (Tab 1), İcra (Tab 7) ve Kapaklar (Tab 8) görünsün
        if (isSentenced === false) {
            return ['1', '7', '8'];
        }
        
        // isSentenced true ise veya tanımlı değilse normal mantık devam etsin
        const stateTitle = arbitrationData?.arbitrationState?.title;
        
        // Her zaman görünür olan temel tablar
        let visibleTabs = ['1', '2', '8']; // Kaza Dosyaları, Tahkim Bilgileri, Kapaklar
        
        if (!stateTitle) {
            // Eğer state yoksa sadece temel tabları döndür
            return visibleTabs;
        }

        // State'e göre yeni eklenebilecek tablar
        switch (stateTitle) {
            case 'Ara Karar Bekleniyor':
            case 'Bilirkişi Ödemesi Bekleniyor':
                visibleTabs.push('3'); // Ara Kararlar
                break;
            
            case 'Bilirkişi Raporu Bekleniyor':
            case 'Islah Ödemesi Bekleniyor':
                visibleTabs.push('3', '4'); // Ara Kararlar + Bilirkişi Raporları
                break;
            
            case 'Karar Bekleniyor':
            case 'Karşı Vekalet Ödemesi Bekleniyor':
            case 'İtiraz Ücreti Ödemesi Bekleniyor':
                visibleTabs.push('3', '4', '5'); // Ara Kararlar + Bilirkişi Raporları + Kararlar
                break;

            case 'İcra Başvuru Bekleniyor':
            // Takip Masrafı ve Tahsil Harcı Ödemesi durumları devre dışı — İcra Başvuru Bekleniyor'dan direkt ekleme yapılıyor
            // case 'Takip Masrafı Ödemesi Bekleniyor':
            // case 'Tahsil Harcı Ödemesi Bekleniyor':
                visibleTabs.push('3', '4', '5', '7'); // Ara Kararlar + Bilirkişi Raporları + Kararlar + İcra
                break;

            case 'Tekrar Başvuru Bekleniyor':
            case 'Tekrar Başvuruda':
            case 'Kapama Bekleniyor':
                visibleTabs.push('3', '4', '5', '6', '7'); // Tüm tablar
                break;
            
            default:
                // Default case - sadece temel tablar
                break;
        }

        // VERİ VARLIĞI KONTROLÜ - Eğer veri varsa o tabları da göster
        // Bu sayede state geri gittiğinde bile önceki veriler görünür kalır
        
        // Ara Kararlar verisi varsa Tab 3'ü göster
        if (arbitrationData?.interlocutoryRulingInformation && arbitrationData.interlocutoryRulingInformation.length > 0) {
            if (!visibleTabs.includes('3')) {
                visibleTabs.push('3');
            }
        }
        
        // Bilirkişi Raporları verisi varsa Tab 4'ü göster
        if (arbitrationData?.expertWitnessReportInformation && arbitrationData.expertWitnessReportInformation.length > 0) {
            if (!visibleTabs.includes('4')) {
                visibleTabs.push('4');
            }
        }
        
        // Kararlar verisi varsa Tab 5'i göster
        if (arbitrationData?.rulingInformation && arbitrationData.rulingInformation !== null) {
            if (!visibleTabs.includes('5')) {
                visibleTabs.push('5');
            }
        }
        
        // Tekrar Başvuru verisi varsa Tab 6'yı göster
        if (arbitrationData?.reapplicationInformation && arbitrationData.reapplicationInformation !== null) {
            if (!visibleTabs.includes('6')) {
                visibleTabs.push('6');
            }
        }

        // Tabları sıralı döndür
        return visibleTabs.sort();
    };

    // Get visible tabs based on current state
    const visibleTabIds = getVisibleTabIds();
    const visibleTabs = tabs.filter(tab => visibleTabIds.includes(tab.id));

    const toggle = tab => {
        // Only allow switching to visible tabs
        if (visibleTabIds.includes(tab) && activeTab !== tab) {
            setActiveTab(tab);
        }
    };

    // Fetch arbitration data
    const fetchArbitrationData = async () => {
        try {
            setLoading(true);
            const response = await fetchFormData({
                apiEndpoint: arbitrationApiUrl,
                page: 1,
                pageSize: 1,
            });
            
            // Extract data based on API response structure
            let data;
            if (response?.isSuccess && response?.data) {
                // New API format with isSuccess/data wrapper
                data = response.data;
            } else if (response) {
                // Old API format with direct data return
                data = response;
            } else {
                // No valid data
                toast.error('Tahkim bilgileri alınamadı');
                return;
            }
            
            if (data) {
                setArbitrationData(data);
            } else {
                toast.error('Tahkim bilgileri bulunamadı');
            }
        } catch (error) {
            toast.error('Tahkim bilgileri alınırken bir hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchArbitrationData();
        }
    }, [id]);

    // Auto-select first visible tab if current active tab is not visible
    useEffect(() => {
        if (!visibleTabIds.includes(activeTab) && visibleTabIds.length > 0) {
            setActiveTab(visibleTabIds[0]);
        }
    }, [arbitrationData?.arbitrationState?.title]);

    if (loading) {
        return (
            <Fragment>
                <Breadcrumbs mainTitle="Tahkim Detayı" parent="Tahkim" title="Detay" />
                <Container fluid={true}>
                    <Row>
                        <Col sm="12">
                            <Card>
                                <CardBody>
                                    <div className="text-center">
                                        <div className="spinner-border" role="status">
                                            <span className="sr-only">Yükleniyor...</span>
                                        </div>
                                        <p className="mt-3">Tahkim bilgileri yükleniyor...</p>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </Fragment>
        );
    }

    return (
        <Fragment>
            <Breadcrumbs 
                mainTitle="Tahkim Detayı" 
                parent="Tahkim" 
                title={`Tahkim #${id}`} 
            />
            <Container fluid={true}>
                <Row>
                    <Col sm="12">
                        {/* Current State Display */}
                        {arbitrationData?.arbitrationState && (
                            <div className="mb-3">
                                <div className="alert alert-info">
                                    <i className="fa fa-info-circle me-2"></i>
                                    <strong>Mevcut Durum:</strong> {arbitrationData.arbitrationState.title}
                                </div>
                            </div>
                        )}

                        {/* Tab Navigation */}
                        <Nav tabs className="nav-tabs-success mb-4">
                            {visibleTabs.map(tab => (
                                <NavItem key={tab.id}>
                                    <NavLink
                                        className={classnames({ active: activeTab === tab.id })}
                                        onClick={() => toggle(tab.id)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <i className={tab.icon} style={{ marginRight: '8px' }}></i>
                                        {tab.title}
                                    </NavLink>
                                </NavItem>
                            ))}
                        </Nav>

                        {/* Tab Content */}
                        <TabContent activeTab={activeTab}>
                            {/* Tab 1: Kaza Dosyaları - Always visible */}
                            {visibleTabIds.includes('1') && (
                                <TabPane tabId="1">
                                    <AccidentFileInformations 
                                        arbitrationId={id} 
                                        arbitrationData={arbitrationData}
                                    />
                                </TabPane>
                            )}

                            {/* Tab 2: Tahkim Bilgileri - Always visible */}
                            {visibleTabIds.includes('2') && (
                                <TabPane tabId="2">
                                    <ArbitrationInformations 
                                        arbitrationId={id} 
                                        arbitrationData={arbitrationData}
                                        refreshArbitrationData={fetchArbitrationData}
                                    />
                                </TabPane>
                            )}

                            {/* Tab 3: Ara Kararlar */}
                            {visibleTabIds.includes('3') && (
                                <TabPane tabId="3">
                                    <InterlocutoryRulingInformations 
                                        arbitrationId={id} 
                                        arbitrationData={arbitrationData}
                                        refreshArbitrationData={fetchArbitrationData}
                                    />
                                </TabPane>
                            )}

                            {/* Tab 4: Bilirkişi Raporları */}
                            {visibleTabIds.includes('4') && (
                                <TabPane tabId="4">
                                    <ExpertWitnessReportInformations 
                                        arbitrationId={id} 
                                        arbitrationData={arbitrationData}
                                        refreshArbitrationData={fetchArbitrationData}
                                    />
                                </TabPane>
                            )}

                            {/* Tab 5: Kararlar */}
                            {visibleTabIds.includes('5') && (
                                <TabPane tabId="5">
                                    <RulingInformations 
                                        arbitrationId={id} 
                                        arbitrationData={arbitrationData}
                                        refreshArbitrationData={fetchArbitrationData}
                                    />
                                </TabPane>
                            )}

                            {/* Tab 6: Tekrar Başvuru Bilgileri */}
                            {visibleTabIds.includes('6') && (
                                <TabPane tabId="6">
                                    <ReapplicationInformations 
                                        arbitrationId={id} 
                                        arbitrationData={arbitrationData}
                                        refreshArbitrationData={fetchArbitrationData}
                                    />
                                </TabPane>
                            )}

                            {/* Tab 7: İcra Bilgileri */}
                            {visibleTabIds.includes('7') && (
                                <TabPane tabId="7">
                                    <EnforcementInformations
                                        arbitrationId={id}
                                        arbitrationData={arbitrationData}
                                        refreshArbitrationData={fetchArbitrationData}
                                    />
                                </TabPane>
                            )}

                            {/* Tab 8: Kapaklar */}
                            {visibleTabIds.includes('8') && (
                                <TabPane tabId="8">
                                    <CompensationInformations
                                        arbitrationId={id}
                                        arbitrationData={arbitrationData}
                                        refreshArbitrationData={fetchArbitrationData}
                                    />
                                </TabPane>
                            )}
                        </TabContent>
                    </Col>
                </Row>
            </Container>
        </Fragment>
    );
};

export default Arbitration;
